// https://ghe.coxautoinc.com/DDC-InternalSystems/account-data-distributor/blob/master/account-data-distributor-model/src/main/java/com/dealer/accountdatadistributor/model/inventory/InventoryListingConfig.java

import * as t from "io-ts";
import type {
  automotiveClassificationTypesValues,
  automotiveClassificationValues,
  statusOptionsValues
} from ".";

export const InventoryListingConfigFromApi = t.type({
  id: t.string,
  accountId: t.string,
  listingAccountId: t.union([t.string, t.null]),
  selectedAccountId: t.union([t.string, t.null]),
  excludedAccountIds: t.union([t.string, t.null]),
  sharedExcludedAccountIds: t.union([t.string, t.null]),
  listingAccountIds: t.union([t.string, t.null]),
  inventoryId: t.string,
  stickyModels: t.union([t.string, t.null]),
  selectedModels: t.union([t.string, t.null]),
  excludedModels: t.union([t.string, t.null]),
  stickyMakes: t.union([t.string, t.null]),
  selectedMakes: t.union([t.string, t.null]),
  excludedMakes: t.union([t.string, t.null]),
  defaultSortBy: t.string,
  compareTarget: t.string,
  listingTarget: t.string,
  stickyModelCodes: t.union([t.string, t.null]),
  selectedModelCodes: t.union([t.string, t.null]),
  excludedModelCodes: t.union([t.string, t.null]),
  imagesDirectory: t.string,
  defaultView: t.string,
  calcCustomTerms: t.union([t.string, t.null]),
  certifiedLogo: t.union([t.string, t.null]),
  stickyTrims: t.union([t.string, t.null]),
  selectedTrims: t.union([t.string, t.null]),
  excludedTrims: t.union([t.string, t.null]),
  stickyOptionCodes: t.union([t.string, t.null]),
  selectedOptionCodes: t.union([t.string, t.null]),
  excludedOptionCodes: t.union([t.string, t.null]),
  stickyBodyStyles: t.union([t.string, t.null]),
  selectedBodyStyles: t.union([t.string, t.null]),
  excludedBodyStyles: t.union([t.string, t.null]),
  stickyFuelTypes: t.union([t.string, t.null]),
  selectedFuelTypes: t.union([t.string, t.null]),
  excludedFuelTypes: t.union([t.string, t.null]),
  stockNumberStartsWith: t.union([t.string, t.null]),
  stockNumberEndsWith: t.union([t.string, t.null]),
  exclusiveAccountId: t.boolean,
  paging: t.boolean,
  carFaxOneOwnerOnly: t.boolean,
  showCalc: t.boolean,
  columns: t.array(
    t.type({
      label: t.union([t.string, t.null]),
      attribute: t.union([t.string, t.null]),
      sortby: t.union([t.string, t.null]),
      numeric: t.boolean
    })
  ),
  sbShowCounts: t.boolean,
  sbShowYear: t.boolean,
  sbShowMake: t.boolean,
  sbShowModel: t.boolean,
  sbShowTrim: t.boolean,
  sbShowBodyStyle: t.boolean,
  sbShowEngine: t.boolean,
  sbShowEngineSize: t.boolean,
  sbShowMileage: t.boolean,
  sbShowPrice: t.boolean,
  sbShowFuel: t.boolean,
  sbShowCityMPG: t.boolean,
  sbShowHighwayMPG: t.boolean,
  sbShowAccount: t.boolean,
  sbShowCertified: t.boolean,
  showStockNumberSearch: t.boolean,
  sbShowClassification: t.boolean,
  sbShowType: t.boolean,
  sbShowExtColor: t.boolean,
  sbShowIntColor: t.boolean,
  sbShowTransmission: t.boolean,
  imaging: t.boolean,
  makeOffer: t.boolean,
  toolTip: t.boolean,
  showTotalCount: t.boolean,
  compare: t.boolean,
  epricer: t.boolean,
  featured: t.boolean,
  smartBrowsing: t.boolean,
  camera: t.boolean,
  priority: t.boolean,
  certified: t.boolean,
  excludeCertified: t.boolean,
  showVideo: t.boolean,
  videoOnly: t.boolean,
  showCertifiedLogo: t.boolean,
  statusList: t.array(t.number),
  status: t.number,
  charLimit: t.number,
  pageSize: t.number,
  visiblePageCount: t.number,
  baseCityMPG: t.number,
  baseHighwayMPG: t.number,
  classificationTypes: t.array(
    t.type({
      classification: t.number,
      type: t.number
    })
  ),
  priceCap: t.number,
  yearCap: t.number,
  minLotDays: t.number,
  sharedMinLotDays: t.number,
  calcRate: t.number,
  calcMaxTerm: t.number,
  calcCashTrade: t.number,
  calcInterval: t.number,
  calcSelectedTerm: t.number,
  priceFloor: t.number,
  priceEndsWith: t.number,
  mileageCap: t.number,
  mileageFloor: t.number,
  maxLotDays: t.number,
  yearFloor: t.number
});
export interface InventoryListingConfig
  extends StringInventoryListingConfig,
    BooleanInventoryListingConfig,
    ClassificationInventoryListingConfig,
    StatusInventoryListingConfig,
    NumberInventoryListingConfig {}

export type possibleValueTypes =
  | string
  | number
  | undefined
  | boolean
  | number[]
  | null
  | ClassificationTypes[];

export const isStringInventoryListingConfig = (
  value: possibleValueTypes
): value is string => {
  return typeof value === "string";
};
export const isStringOrNullInventoryListingConfig = (
  value: possibleValueTypes
): value is StringOrNull => {
  return typeof value === "string" || value === null;
};

type StringOrNull = null | string;

export const isBooleanInventoryListingConfig = (
  value: possibleValueTypes
): value is boolean => {
  return typeof value === "boolean";
};

export const isClassificationInventoryListingConfig = (
  value: possibleValueTypes
): value is ClassificationTypes[] => {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof (value[0] as ClassificationTypes).type !== "undefined"
  );
};

export const isClassificationInventoryListingConfigOrEmpty = (
  value: possibleValueTypes
): value is ClassificationTypes[] => {
  return (
    Array.isArray(value) &&
    (value.length === 0 ||
      typeof (value[0] as ClassificationTypes).type !== "undefined")
  );
};

export const isNumberInventoryListingConfig = (
  value: possibleValueTypes
): value is number => {
  return typeof value === "number";
};

export type classificationChckboxes =
  | "All Classes"
  | "Primary"
  | "Fleet"
  | "Wholesale"
  | "Exotic"
  | "Classifieds";
export type classificationRadioButtons = "All Types" | "Used" | "New";

export type InventoryListingConfigKeys = keyof InventoryListingConfig;

export interface StringInventoryListingConfig {
  id: string;
  accountId: string;
  listingAccountId?: string | null;
  selectedAccountId: string | null;
  excludedAccountIds: string | null;
  sharedExcludedAccountIds: string | null;
  listingAccountIds: string | null;
  inventoryId: string;
  stickyModels?: string | null;
  selectedModels: string | null;
  excludedModels: string | null;
  stickyMakes?: string | null;
  selectedMakes?: string | null;
  excludedMakes: string | null;
  defaultSortBy: string;
  compareTarget: string;
  listingTarget: string;
  stickyModelCodes: string | null;
  selectedModelCodes: string | null;
  excludedModelCodes: string | null;
  imagesDirectory: string;
  defaultView: string;
  calcCustomTerms: string | null;
  certifiedLogo: string | null;
  stickyTrims: string | null;
  selectedTrims?: string | null;
  excludedTrims: string | null;
  stickyOptionCodes: string | null;
  selectedOptionCodes: string | null;
  excludedOptionCodes: string | null;
  stickyBodyStyles: string | null;
  selectedBodyStyles: string | null;
  excludedBodyStyles: string | null;
  stickyFuelTypes: string | null;
  selectedFuelTypes: string | null;
  excludedFuelTypes: string | null;
  stockNumberStartsWith: string | null;
  stockNumberEndsWith: string | null;
}
export interface BooleanInventoryListingConfig {
  exclusiveAccountId: boolean;
  paging: boolean;
  carFaxOneOwnerOnly: boolean;
  showCalc: boolean;
  sbShowCounts: boolean;
  sbShowYear: boolean;
  sbShowMake: boolean;
  sbShowModel: boolean;
  sbShowTrim: boolean;
  sbShowBodyStyle: boolean;
  sbShowEngine: boolean;
  sbShowEngineSize: boolean;
  sbShowMileage: boolean;
  sbShowPrice: boolean;
  sbShowFuel: boolean;
  sbShowCityMPG: boolean;
  sbShowHighwayMPG: boolean;
  sbShowAccount: boolean;
  sbShowCertified: boolean;
  showStockNumberSearch: boolean;
  sbShowClassification: boolean;
  sbShowType: boolean;
  sbShowExtColor: boolean;
  sbShowIntColor: boolean;
  sbShowTransmission: boolean;
  imaging: boolean;
  makeOffer: boolean;
  toolTip: boolean;
  showTotalCount: boolean;
  compare: boolean;
  epricer: boolean;
  featured: boolean;
  smartBrowsing: boolean;
  camera: boolean;
  priority: boolean;
  certified: boolean;
  excludeCertified: boolean;
  showVideo: boolean;
  videoOnly: boolean;
  showCertifiedLogo: boolean;
}
export interface ClassificationInventoryListingConfig {
  classificationTypes: ClassificationTypes[];
}
export interface StatusInventoryListingConfig {
  statusList: statusOptionsValues[];
}
export interface ColumnInventoryListingConfig {
  columns: Column[];
}
export interface NumberInventoryListingConfig {
  status: statusOptionsValues;
  charLimit: number;
  pageSize: number;
  visiblePageCount: number;
  baseCityMPG: number;
  baseHighwayMPG: number;
  priceCap: number;
  yearCap: number;
  minLotDays: number;
  sharedMinLotDays: number;
  calcRate: number;
  calcMaxTerm: number;
  calcCashTrade: number;
  calcInterval: number;
  calcSelectedTerm: number;
  priceFloor: number;
  priceEndsWith: number;
  mileageCap: number;
  mileageFloor: number;
  maxLotDays: number;
  yearFloor: number;
}
export interface Column {
  label?: string | null;
  attribute?: ColumnAttributes | null;
  sortby?: ColumnSortby | null;
  numeric: boolean | null;
  index: number;
}

export const defaultConfigs = [
  "new",
  "used",
  "bargain",
  "certified-used",
  "fleet-new",
  "fleet-used",
  "wholesale-new",
  "wholesale-used",
  "exotic-new",
  "exotic-used",
  "new-mini",
  "used-mini",
  "all"
];

export interface ClassificationTypes {
  classification: automotiveClassificationValues;
  type: automotiveClassificationTypesValues;
}

export type ColumnAttributes =
  | "AccountName"
  | "BodyStyle"
  | "ExtColor"
  | "InternetPrice"
  | "AskingPrice"
  | "InvoicePrice"
  | "Make"
  | "Mileage"
  | "Model"
  | "Msrp"
  | "StockNumber"
  | "Year"
  | "Certified"
  | "Status"
  | "EstPayment"
  | "RetailValue"
  | "SalePrice"
  | "ModelCode"
  | "Transmission"
  | "Engine"
  | "EngineSize"
  | "WholesalePrice"
  | "DriveLine"
  | "EbayAuctionId"
  | "FuelType"
  | "CityMPG"
  | "HighwayMPG";

type ColumnSortby =
  | "accountname,make,model,trimlevel,year desc"
  | "bodystyle,make,model,trimlevel,year desc"
  | "extcolor,make,model,trimlevel,year desc"
  | "internetprice,make,model,trimlevel,year desc"
  | "askingprice,make,model,trimlevel,year desc"
  | "invoiceprice,make,model,trimlevel,year desc"
  | "make,model,trimlevel,year desc"
  | "mileage,make,model,trimlevel,year desc"
  | "model,make,trimlevel,year desc"
  | "msrp,make,model,trimlevel,year desc"
  | "stocknumber,make,model,trimlevel,year desc"
  | "year desc,make,model,trimlevel"
  | "certified desc,make,model,trimlevel,year desc"
  | "status desc,make,model,trimlevel,year desc"
  | "internetprice,make,model,trimlevel,year desc"
  | "retailvalue,make,model,trimlevel,year desc"
  | "saleprice,make,model,trimlevel,year desc"
  | "modelcode,make,model,trimlevel,year desc"
  | "transmission,make,model,trimlevel,year desc"
  | "engine,enginesize,make,model,trimlevel,year desc"
  | "enginesize,make,model,trimlevel,year desc"
  | "wholesaleprice,make,model,trimlevel,year desc"
  | "driveline,make,model,trimlevel,year desc"
  | "ebay_auction_id,make,model,trimlevel,year desc"
  | "fuel,make,model,trimlevel,year desc"
  | "city_mpg,make,model,trimlevel,year desc"
  | "highway_mpg,make,model,trimlevel,year desc";

export const attributeSortByMap: Record<ColumnAttributes, ColumnSortby> = {
  AccountName: "accountname,make,model,trimlevel,year desc",
  BodyStyle: "bodystyle,make,model,trimlevel,year desc",
  ExtColor: "extcolor,make,model,trimlevel,year desc",
  InternetPrice: "internetprice,make,model,trimlevel,year desc",
  AskingPrice: "askingprice,make,model,trimlevel,year desc",
  InvoicePrice: "invoiceprice,make,model,trimlevel,year desc",
  Make: "make,model,trimlevel,year desc",
  Mileage: "mileage,make,model,trimlevel,year desc",
  Model: "model,make,trimlevel,year desc",
  Msrp: "msrp,make,model,trimlevel,year desc",
  StockNumber: "stocknumber,make,model,trimlevel,year desc",
  Year: "year desc,make,model,trimlevel",
  Certified: "certified desc,make,model,trimlevel,year desc",
  Status: "status desc,make,model,trimlevel,year desc",
  EstPayment: "internetprice,make,model,trimlevel,year desc",
  RetailValue: "retailvalue,make,model,trimlevel,year desc",
  SalePrice: "saleprice,make,model,trimlevel,year desc",
  ModelCode: "modelcode,make,model,trimlevel,year desc",
  Transmission: "transmission,make,model,trimlevel,year desc",
  Engine: "engine,enginesize,make,model,trimlevel,year desc",
  EngineSize: "enginesize,make,model,trimlevel,year desc",
  WholesalePrice: "wholesaleprice,make,model,trimlevel,year desc",
  DriveLine: "driveline,make,model,trimlevel,year desc",
  EbayAuctionId: "ebay_auction_id,make,model,trimlevel,year desc",
  FuelType: "fuel,make,model,trimlevel,year desc",
  CityMPG: "city_mpg,make,model,trimlevel,year desc",
  HighwayMPG: "highway_mpg,make,model,trimlevel,year desc"
};

export const attributeNumericMap: Record<ColumnAttributes, boolean> = {
  AccountName: false,
  BodyStyle: false,
  ExtColor: false,
  InternetPrice: true,
  AskingPrice: true,
  InvoicePrice: true,
  Make: false,
  Mileage: true,
  Model: false,
  Msrp: true,
  StockNumber: false,
  Year: true,
  Certified: false,
  Status: false,
  EstPayment: true,
  RetailValue: true,
  SalePrice: true,
  ModelCode: false,
  Transmission: false,
  Engine: false,
  EngineSize: false,
  WholesalePrice: true,
  DriveLine: false,
  EbayAuctionId: false,
  FuelType: false,
  CityMPG: false,
  HighwayMPG: false
};
// static {
//   attributeSortByMap.put( "AccountName", "accountname,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "BodyStyle", "bodystyle,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "ExtColor", "extcolor,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "InternetPrice", "internetprice,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "AskingPrice", "askingprice,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "InvoicePrice", "invoiceprice,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "Make", "make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "Mileage", "mileage,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "Model", "model,make,trimlevel,year desc" );
//   attributeSortByMap.put( "Msrp", "msrp,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "StockNumber", "stocknumber,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "Year", "year desc,make,model,trimlevel" );
//   attributeSortByMap.put( "Certified", "certified desc,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "Status", "status desc,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "EstPayment", "internetprice,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "RetailValue", "retailvalue,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "SalePrice", "saleprice,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "ModelCode", "modelcode,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "Transmission", "transmission,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "Engine", "engine,enginesize,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "EngineSize", "enginesize,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "WholesalePrice", "wholesaleprice,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "DriveLine", "driveline,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "EbayAuctionId", "ebay_auction_id,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "FuelType", "fuel,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "CityMPG", "city_mpg,make,model,trimlevel,year desc" );
//   attributeSortByMap.put( "HighwayMPG", "highway_mpg,make,model,trimlevel,year desc" );
// }
// static {
//   attributeNumericMap.put( "AccountName", "false" );
//   attributeNumericMap.put( "BodyStyle", "false" );
//   attributeNumericMap.put( "ExtColor", "false" );
//   attributeNumericMap.put( "InternetPrice", "true" );
//   attributeNumericMap.put( "AskingPrice", "true" );
//   attributeNumericMap.put( "InvoicePrice", "true" );
//   attributeNumericMap.put( "Make", "false" );
//   attributeNumericMap.put( "Mileage", "true" );
//   attributeNumericMap.put( "Model", "false" );
//   attributeNumericMap.put( "Msrp", "true" );
//   attributeNumericMap.put( "StockNumber", "false" );
//   attributeNumericMap.put( "Year", "true" );
//   attributeNumericMap.put( "Certified", "false" );
//   attributeNumericMap.put( "Status", "false" );
//   attributeNumericMap.put( "EstPayment", "true" );
//   attributeNumericMap.put( "RetailValue", "true" );
//   attributeNumericMap.put( "SalePrice", "true" );
//   attributeNumericMap.put( "ModelCode", "false" );
//   attributeNumericMap.put( "Transmission", "false" );
//   attributeNumericMap.put( "Engine", "false" );
//   attributeNumericMap.put( "EngineSize", "false" );
//   attributeNumericMap.put( "WholesalePrice", "true" );
//   attributeNumericMap.put( "DriveLine", "false" );
//   attributeNumericMap.put( "EbayAuctionId", "false" );
//   attributeNumericMap.put( "FuelType", "false" );
//   attributeNumericMap.put( "CityMPG", "false" );
//   attributeNumericMap.put( "HighwayMPG", "false" );
// }
